import { Kanban } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  CRM_CARD_FIELD_KEYS,
  chatTitle,
  shownPhone,
  type ChatDetail,
  type CrmActivityItem,
} from '@/entities/chats';
import { useAuth } from '@/features/auth';
import { formatDateTime, crmName, labelOf, useI18n, type Locale, type Messages } from '@/shared/i18n';
import { Button } from '@/shared/ui/shadcn/button';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';

function Field({ label, value }: { label: string; value?: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-[11px] tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className={value ? 'text-sm break-words whitespace-pre-wrap' : 'text-sm text-muted-foreground'}>
        {value || '—'}
      </dd>
    </div>
  );
}

function activityFieldLabel(t: Messages, field?: string) {
  const key = field?.trim() ?? '';
  if (!key) return '';
  switch (key) {
    case 'name':
      return t.crm.nameLabel;
    case 'pipeline':
      return t.common.funnel;
    case 'stage':
      return t.common.stage;
    case 'phone':
      return t.common.phone;
    case 'summary':
    case 'description':
      return t.crm.summary;
    case 'price':
      return t.fields.budget;
    case 'responsible':
      return t.crm.responsible;
    case 'lead_bucket':
      return t.crm.leadBucket;
    case 'lead_source':
      return t.crm.leadSource;
    default:
      return labelOf(t.fields, key);
  }
}

function formatActivityValue(t: Messages, field: string | undefined, value: string | undefined) {
  const text = value?.trim() ?? '';
  if (!text) return t.crm.activityCleared;
  if (field === 'pipeline' || field === 'stage') return crmName(t.crm.names, text);
  if (field === 'price') {
    const amount = Number(text);
    if (Number.isFinite(amount) && amount > 0) return `${amount.toLocaleString('ru-RU')} ฿`;
  }
  if (field === 'lead_bucket' || field === 'lead_source') return labelOf(t.fields, text) || text;
  return text;
}

function activitySourceLabel(t: Messages, source?: string) {
  const value = source?.trim().toLowerCase() ?? '';
  if (value === 'agatha') return 'Agatha';
  if (value === 'operator') return t.crm.activityFromOperator;
  if (value === 'amocrm') return t.crm.activityFromAmo;
  return source?.trim() ?? '';
}

function ActivityRow({
  item,
  t,
  locale,
}: {
  item: CrmActivityItem;
  t: Messages;
  locale: Locale;
}) {
  const when = item.created_at ? formatDateTime(item.created_at, locale) : '';
  if (item.kind === 'field_change' || item.kind === 'stage_change') {
    const label = activityFieldLabel(t, item.field);
    const source = activitySourceLabel(t, item.source);
    return (
      <li className="space-y-1">
        <p className="text-[11px] text-muted-foreground">
          {[source || item.author, when].filter(Boolean).join(' · ')}
        </p>
        <p className="text-sm break-words">
          <span className="text-muted-foreground">{label}: </span>
          {formatActivityValue(t, item.field, item.old_value)}
          {' → '}
          {formatActivityValue(t, item.field, item.new_value)}
        </p>
        {item.text?.trim() ? (
          <p className="text-sm break-words whitespace-pre-wrap">{item.text}</p>
        ) : null}
      </li>
    );
  }
  return (
    <li className="space-y-1">
      <p className="text-[11px] text-muted-foreground">
        {[item.author || activitySourceLabel(t, item.source), when].filter(Boolean).join(' · ')}
      </p>
      <p className="text-sm break-words whitespace-pre-wrap">{item.text}</p>
    </li>
  );
}

export function ClientChatAside({ chat }: { chat: ChatDetail }) {
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const cardId = chat.card_id?.trim() ?? '';
  const canOpenCrm = Boolean(cardId) && hasPermission('can_view_crm');
  return (
    <ScrollArea className="h-full">
      <div className="flex items-start gap-2 border-b px-4 py-3">
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">
          {chatTitle(chat) || t.common.lead}
        </h2>
        {canOpenCrm ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 gap-1.5 px-2 text-xs"
            render={<Link to={`/crm/cards/${cardId}`} />}
          >
            <Kanban className="size-3.5" />
            {t.crm.openCard}
          </Button>
        ) : null}
      </div>
      <dl className="space-y-3 px-4 py-4">
        <Field
          label={t.common.phone}
          value={shownPhone(chat.phone, chat.channel_chat_external_id)}
        />
        <Field label={t.common.channel} value={labelOf(t.channels, chat.channel)} />
        <Field label={t.common.role} value={labelOf(t.roles, chat.role)} />
        <Field
          label={t.dialogStatus.label}
          value={labelOf(t.dialogStatus, chat.status)}
        />
        <Field label={t.common.funnel} value={crmName(t.crm.names, chat.pipeline)} />
        <Field label={t.common.stage} value={crmName(t.crm.names, chat.stage)} />
        <Field label={t.crm.summary} value={chat.summary} />
        {CRM_CARD_FIELD_KEYS.map((key) => (
          <Field key={key} label={labelOf(t.fields, key)} value={chat.fields?.[key]} />
        ))}
      </dl>
      <section className="border-t px-4 py-4">
        <h3 className="text-[11px] tracking-wide text-muted-foreground uppercase">
          {t.crm.activity}
        </h3>
        {(chat.activity?.length ?? 0) === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">{t.crm.activityEmpty}</p>
        ) : (
          <ol className="mt-3 space-y-3">
            {chat.activity?.map((item) => (
              <ActivityRow key={`${item.kind}-${item.id}`} item={item} t={t} locale={locale} />
            ))}
          </ol>
        )}
      </section>
    </ScrollArea>
  );
}
